/**
 * Wave chat-instant · ChatNotificationDock
 *
 * Bubbles flutuantes bottom-right que avisam o user de mensagens novas
 * SEM precisar abrir o panel. Estilo WhatsApp Web "chat heads".
 *
 * Detection: a gente NÃO tem WebSocket pra chat-instant. O signal de
 * "msg nova" vem do polling de listConversations que ChatLauncher já
 * faz a cada 8s. A cada update do store, comparamos lastMessage.id
 * (e/ou unreadCount) com o snapshot anterior:
 *   - Se uma conv tem lastMessage com authorUserId !== me e id diferente
 *     do que vimos por último → é uma msg nova.
 *   - Não tocamos no estado de "lido" — isso é responsabilidade do panel
 *     quando ele abre. A bubble é só um ping visual.
 *
 * Anti-flood / aggregation:
 *   - Mesmo sender dentro de 60s → agrega no mesmo card ("Anderson · 3
 *     novas") sem stackar novos cards.
 *   - Senders diferentes → cada um vira um card, empilhados verticalmente.
 *   - Cap visual em 3 cards. Excedente vira chip "+ N outras" no rodapé.
 *   - Auto-dismiss: 8s por card a partir da última msg agregada.
 *   - Click no card → openThreadInPanel(convId) (substitui inline no panel).
 *
 * Reset:
 *   - Panel abrindo → limpa todas as bubbles (o user já viu).
 *   - Conv abrindo na thread inline → limpa bubble daquela conv.
 *   - Não persiste em reload — só vive na sessão.
 *
 * Z-index: z-[55] — abaixo do panel (z-[60]) e seu backdrop (z-[59]),
 * acima do chrome (z-50 da TopBar).
 *
 * Sound: NUNCA. JP foi explícito (2026-05-21): "Sound: NO. Visual only."
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/store/auth.store';
import {
  useChatInstantStore,
  selectActivePills,
  selectPillOverflowCount,
} from '@/store/chat-instant.store';
import type { ChatInstantConversation } from '@/api/chat-instant';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

const AUTO_DISMISS_MS = 8_000;
const AGGREGATE_WINDOW_MS = 60_000;
const MAX_VISIBLE = 3;

interface DockBubble {
  conversationId: string;
  senderUserId: string;
  senderName: string;
  senderAvatarUrl: string | null;
  preview: string;
  count: number;
  /** ms epoch da msg mais recente agregada (pra auto-dismiss). */
  lastMessageAt: number;
  /** id da msg mais recente que disparou a notificação. */
  lastMessageId: string;
}

export function ChatNotificationDock() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const me = useAuth((s) => s.user);
  const conversations = useChatInstantStore((s) => s.conversations);
  const panelOpen = useChatInstantStore((s) => s.panelOpen);
  const panelView = useChatInstantStore((s) => s.panelView);
  const activeConvId = useChatInstantStore((s) => s.panelActiveConversationId);
  const openThreadInPanel = useChatInstantStore((s) => s.openThreadInPanel);
  // Wave chat-pills · 2026-05-21 — incoming msgs pin the pill too.
  const pinConversation = useChatInstantStore((s) => s.pinConversation);
  // Pills row pode estar visível no canto bottom-3 — neste caso o dock
  // se levanta pra bottom-20 evitando overlap visual horizontal.
  const activePillsCount = useChatInstantStore(
    (s) => selectActivePills(s).length,
  );
  const pillOverflow = useChatInstantStore(selectPillOverflowCount);
  const hasPillsBelow = activePillsCount > 0 || pillOverflow > 0;

  /**
   * Snapshot do último lastMessage.id que vimos pra cada conv. Usado pra
   * detectar se "um novo lastMessage chegou" entre dois polls. Inicia
   * vazio e seedamos no primeiro render com o estado atual — assim NÃO
   * disparamos notificações pra mensagens que já existiam quando o user
   * abriu a aba (evita "barulho" no boot).
   */
  const seenLastIdsRef = useRef<Map<string, string>>(new Map());
  const seededRef = useRef(false);

  const [bubbles, setBubbles] = useState<DockBubble[]>([]);

  // Seed inicial — não dispara nada pro estado de boot.
  useEffect(() => {
    if (seededRef.current || !me) return;
    if (conversations.length === 0) return;
    for (const c of conversations) {
      if (c.lastMessage) {
        seenLastIdsRef.current.set(c.id, c.lastMessage.id);
      }
    }
    seededRef.current = true;
  }, [conversations, me]);

  // Detect new messages a cada update do store.
  useEffect(() => {
    if (!me || !seededRef.current) return;

    const now = Date.now();
    const fresh: DockBubble[] = [];

    for (const c of conversations) {
      const last = c.lastMessage;
      if (!last) continue;
      const prevId = seenLastIdsRef.current.get(c.id);
      if (prevId === last.id) continue;
      // Atualiza snapshot independente de quem mandou (mantém invariante).
      seenLastIdsRef.current.set(c.id, last.id);
      // Só dispara notificação pra msgs DOS OUTROS.
      if (last.authorUserId === me.id) continue;
      // Auto-pin: cria/renova pill DA conv pra qualquer msg recebida —
      // mesmo se o panel estiver aberto na thread (mantém a hierarquia
      // consistente). pinConversation interpreta `userIsLookingHere` e
      // não incrementa unread quando esse for o caso.
      pinConversation(c.id, { incomingUnread: true });
      // Se o panel está aberto E a thread ativa é essa conv, NÃO notifica
      // (o user já tá olhando). Apenas atualiza o snapshot acima.
      if (panelOpen && panelView === 'thread' && activeConvId === c.id) continue;

      fresh.push({
        conversationId: c.id,
        senderUserId: last.authorUserId,
        senderName: senderNameOf(c, last.authorUserId, me.id, isEn),
        senderAvatarUrl: senderAvatarOf(c, last.authorUserId, me.id),
        preview: last.body,
        count: 1,
        lastMessageAt: now,
        lastMessageId: last.id,
      });
    }

    if (fresh.length === 0) return;

    setBubbles((prev) => mergeBubbles(prev, fresh));
  }, [conversations, me, panelOpen, panelView, activeConvId, pinConversation]);

  // Quando o panel abre (lista) → limpa todas as bubbles (user já viu o
  // canal). Quando a thread abre numa conv → limpa só a daquela conv.
  useEffect(() => {
    if (panelOpen && panelView === 'list') {
      setBubbles([]);
      return;
    }
    if (panelOpen && panelView === 'thread' && activeConvId) {
      setBubbles((prev) => prev.filter((b) => b.conversationId !== activeConvId));
    }
  }, [panelOpen, panelView, activeConvId]);

  // Auto-dismiss timer por bubble — a cada tick, expira as que passaram
  // AUTO_DISMISS_MS desde a última msg agregada. 1 timer único pra todas.
  useEffect(() => {
    if (bubbles.length === 0) return;
    const id = window.setInterval(() => {
      setBubbles((prev) => {
        const now = Date.now();
        const next = prev.filter((b) => now - b.lastMessageAt < AUTO_DISMISS_MS);
        return next.length === prev.length ? prev : next;
      });
    }, 1_000);
    return () => window.clearInterval(id);
  }, [bubbles.length]);

  const visible = useMemo(() => bubbles.slice(-MAX_VISIBLE), [bubbles]);
  const hiddenCount = Math.max(0, bubbles.length - visible.length);

  if (!me) return null;
  if (bubbles.length === 0) return null;

  const handleClick = (b: DockBubble) => {
    openThreadInPanel(b.conversationId);
    setBubbles((prev) => prev.filter((x) => x.conversationId !== b.conversationId));
  };

  const handleDismiss = (b: DockBubble) => {
    setBubbles((prev) => prev.filter((x) => x.conversationId !== b.conversationId));
  };

  return (
    <div
      data-testid="chat-instant-notification-dock"
      aria-live="polite"
      aria-label={t.chatInstant.panel.title}
      className={cn(
        // Bottom-right, acima do safe area, abaixo do panel (z-[60]) e
        // backdrop (z-[59]). z-[55] fica acima da chrome (z-50).
        'fixed z-[55] flex flex-col items-end gap-2 transition-[bottom] duration-200 ease-out',
        // Mobile: centralizado horizontalmente pra não bater com FAB de
        // outras features; desktop: fixado direita com 12px.
        'left-3 right-3 md:left-auto md:right-3',
        // Quando pills estão visíveis no bottom-3, dock se levanta pra
        // bottom-20 (acima da linha de 44px das pills + folga). Senão,
        // ancora normal em bottom-3.
        hasPillsBelow ? 'bottom-20 md:bottom-20' : 'bottom-3 md:bottom-3',
        // Não bloqueia o conteúdo atrás (eventos passam) — exceto nos
        // próprios cards (que têm seu pointer-events default).
        'pointer-events-none',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {hiddenCount > 0 && (
        <div
          className={cn(
            'pointer-events-auto rounded-full border border-line bg-surface-2 px-3 py-1',
            'text-[10px] font-medium text-ink-muted shadow-pop dark:shadow-pop-dark',
            'animate-fade-in',
          )}
        >
          {t.chatInstant.dock.moreSenders(hiddenCount)}
        </div>
      )}
      {visible.map((b) => (
        <DockBubbleCard
          key={b.conversationId}
          bubble={b}
          onClick={() => handleClick(b)}
          onDismiss={() => handleDismiss(b)}
          dismissLabel={t.chatInstant.dock.dismiss}
          openLabel={t.chatInstant.dock.openWith(b.senderName)}
          countLabel={t.chatInstant.dock.newCount(b.count)}
        />
      ))}
    </div>
  );
}

// ── Card individual ──────────────────────────────────────────────────

function DockBubbleCard({
  bubble,
  onClick,
  onDismiss,
  dismissLabel,
  openLabel,
  countLabel,
}: {
  bubble: DockBubble;
  onClick: () => void;
  onDismiss: () => void;
  dismissLabel: string;
  openLabel: string;
  countLabel: string;
}) {
  const initials = bubble.senderName
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div
      data-testid="chat-instant-notification-bubble"
      className={cn(
        'pointer-events-auto group relative flex w-full max-w-[280px] items-center gap-2.5',
        'rounded-2xl border border-line bg-surface-2 pl-2.5 pr-1.5 py-2',
        'shadow-pop dark:shadow-pop-dark',
        // Entra com slide-in da direita, igual o panel desktop pra coerência.
        'animate-[dockBubbleIn_220ms_cubic-bezier(0.32,0.72,0,1)_both]',
      )}
    >
      <style>{`
        @keyframes dockBubbleIn {
          0% { transform: translateX(12px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <button
        type="button"
        onClick={onClick}
        aria-label={openLabel}
        className={cn(
          'flex flex-1 min-w-0 items-center gap-2.5 text-left',
          'focus-ring rounded-xl',
        )}
      >
        <div className="relative shrink-0">
          {bubble.senderAvatarUrl ? (
            <img
              src={bubble.senderAvatarUrl}
              alt={bubble.senderName}
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/15 text-[11px] font-semibold text-brand-600">
              {initials || '?'}
            </div>
          )}
          {bubble.count > 1 && (
            <span
              data-testid="chat-instant-notification-count"
              className={cn(
                'absolute -bottom-1 -right-1 inline-flex h-[16px] min-w-[16px] items-center justify-center',
                'rounded-full bg-brand-500 px-1 text-[9px] font-semibold text-white ring-2 ring-surface-2',
              )}
            >
              {bubble.count > 9 ? '9+' : bubble.count}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="truncate text-xs font-semibold text-ink">
              {bubble.senderName}
            </span>
            {bubble.count > 1 && (
              <span className="shrink-0 text-[10px] text-brand-600 font-medium">
                · {countLabel}
              </span>
            )}
          </div>
          <p className="truncate text-[11px] text-ink-muted leading-snug">
            {bubble.preview || '—'}
          </p>
        </div>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={dismissLabel}
        className={cn(
          'pressable shrink-0 flex h-9 w-9 items-center justify-center rounded-full',
          'text-ink-soft hover:bg-surface-3 hover:text-ink transition-colors duration-120',
        )}
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function senderNameOf(
  conv: ChatInstantConversation,
  authorUserId: string,
  meId: string,
  isEn: boolean,
): string {
  if (conv.type === 'group') {
    const member = conv.members.find((m) => m.userId === authorUserId);
    const groupName = conv.name ?? (isEn ? 'Group' : 'Grupo');
    return member ? `${member.fullName} (${groupName})` : groupName;
  }
  const peer = conv.members.find((m) => m.userId !== meId);
  return peer?.fullName ?? (isEn ? 'Someone' : 'Alguém');
}

function senderAvatarOf(
  conv: ChatInstantConversation,
  authorUserId: string,
  meId: string,
): string | null {
  if (conv.type === 'group') {
    return conv.members.find((m) => m.userId === authorUserId)?.avatarUrl ?? null;
  }
  return conv.members.find((m) => m.userId !== meId)?.avatarUrl ?? null;
}

/**
 * Merge das bubbles novas dentro do estado atual. Regra:
 *  - Se já existe bubble pra essa conversation E foi atualizada nos
 *    últimos AGGREGATE_WINDOW_MS → incrementa count e atualiza preview.
 *  - Senão (ou conv diferente) → adiciona como nova bubble.
 *  - Não promove bubbles antigas pro topo se nenhuma msg nova chegou —
 *    a ordem reflete a recência da MENSAGEM, não da bubble.
 */
function mergeBubbles(prev: DockBubble[], fresh: DockBubble[]): DockBubble[] {
  const now = Date.now();
  const byConv = new Map<string, DockBubble>();
  for (const b of prev) byConv.set(b.conversationId, b);

  for (const incoming of fresh) {
    const existing = byConv.get(incoming.conversationId);
    if (existing && now - existing.lastMessageAt < AGGREGATE_WINDOW_MS) {
      byConv.set(incoming.conversationId, {
        ...existing,
        preview: incoming.preview,
        count: existing.count + 1,
        lastMessageAt: incoming.lastMessageAt,
        lastMessageId: incoming.lastMessageId,
        // mantém sender data — não muda numa DM, e em grupo mostra o
        // último autor que justamente é o incoming.
        senderName: incoming.senderName,
        senderAvatarUrl: incoming.senderAvatarUrl,
        senderUserId: incoming.senderUserId,
      });
    } else {
      byConv.set(incoming.conversationId, incoming);
    }
  }

  // Ordena por lastMessageAt asc — os mais recentes ficam no fim do array
  // e portanto no fim do slice(-MAX_VISIBLE) (visíveis em baixo, mais
  // próximos do viewport bottom).
  return [...byConv.values()].sort((a, b) => a.lastMessageAt - b.lastMessageAt);
}
